import cn from 'classnames';
import SocialLinks from 'components/SocialLinks';
import getEarningReportPageTestId from 'constants/testsIds/earningsReportPage';
import useBreakpoint from 'hooks/useBreakpoint';
import { useEffect, useState } from 'react';
import type { EventData } from 'services/Gtm/entities';
import * as S from './SocialShareDropdownStyles';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

interface Props {
  title: string;
  withoutMargins?: boolean;
  trackingData?: EventData;
  className?: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
}

const SocialShareDropdown = ({
  className,
  title,
  withoutMargins = false,
  trackingData,
  variant = 'primary',
}: Props) => {
  const [showContent, setShowContent] = useState(false);
  const isMobile = useBreakpoint('mdMax');
  const { userFlags: userVariants } = useOptimizely();

  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  const variants = userVariants?.socialShareV2 as { variant: string; enabled: boolean };
  const variantToUse = variants?.variant;
  const enabled = variantToUse !== 'off' && variants?.enabled;
  const isEnabled = isClient ? enabled || false : false;

  const onClickHandler = () => {
    setShowContent((sC) => !sC);
    userVariants?.user?.trackEvent('social_share_click', {});
  };

  const closeDropdown = () => {
    setShowContent(false);
  };

  const isSecondary = variant === 'secondary';
  const isTertiary = variant === 'tertiary';
  const mobileShow = isMobile && showContent;

  return (
    <>
      {isSecondary && (
        <S.Overlay
          data-cy={getEarningReportPageTestId('OVERLAY')}
          isVisible={showContent}
          onClick={closeDropdown}
        />
      )}
      <S.Wrapper className={cn({ shareEnabled: isEnabled }, variant)}>
        <S.DropdownContainer
          className={cn(variant, variantToUse, {
            'without-margins': withoutMargins,
            shareEnabled: isEnabled,
          })}
        >
          {/** biome-ignore lint/a11y/useSemanticElements: using old button */}
          <div
            data-cy={getEarningReportPageTestId('SHARE_ICON')}
            role='button'
            className={cn('icon', className, {
              close: showContent && isSecondary,
              share: !(showContent && isSecondary),
              showShare: isEnabled,
            })}
            tabIndex={0}
            onClick={onClickHandler}
            onKeyDown={onClickHandler}
            aria-label='share-button'
          >
            {isEnabled && !isSecondary && <span className={cn('shareText', variantToUse)}>SHARE</span>}
          </div>
          {isTertiary ? <p className='title'>Share</p> : null}
          <S.Dropdown className={cn({ show: mobileShow, shareEnabled: isEnabled }, variant)}>
            <S.DropdownContent className={cn({ 'list-page-social': isSecondary, shareEnabled: isEnabled })}>
              <SocialLinks
                title={title}
                variant={`socialShare_${variantToUse}`}
                isListPage={isSecondary}
                className='social-links-container'
                trackingData={trackingData}
              />
            </S.DropdownContent>
          </S.Dropdown>
        </S.DropdownContainer>
      </S.Wrapper>
    </>
  );
};

export default SocialShareDropdown;
